import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { Group, Loader, Text } from '@mantine/core';
import { Sparkles } from 'lucide-react';
import { SurfaceCard } from '@/design/SurfaceCard';
import CalloutList from '@/shared/surfaces/CalloutList';
import type { TopicCallout } from '@/shared/surfaces/TopicNarration';
import { fetchTopicInsights } from '@/shared/surfaces/TopicInsightsClient';
import type { TopicInsightsRequest } from '@/shared/assist/TopicInsightsContract';
import type { ProviderCostGroup } from '@/features/licenses/LicenseCostPageViewModel';

interface SpendCostInsightsProps {
    providerCosts: ProviderCostGroup[];
    grandTotal: number;
    scopeKey: string;
}

function buildRequest(providerCosts: ProviderCostGroup[], grandTotal: number): TopicInsightsRequest {
    return {
        surfaceTitle: 'Spend',
        topicTitle: 'Cost',
        question: 'Where is the AI budget landing, and what stands out about how spend is distributed across providers?',
        lensLabel: 'provider',
        metricLabel: 'Spend',
        totals: { spend: grandTotal },
        rows: providerCosts
            .map((group) => ({
                label: group.displayName,
                value: group.total,
                share: grandTotal > 0 ? group.total / grandTotal : 0,
            }))
            .sort((a, b) => b.value - a.value),
    };
}

/**
 * The Cost screen's AI insights card. Sends a small pre-aggregated picture of
 * provider spend to the BFF's LLM insights route and renders the returned
 * bullets; a failed or empty reply simply renders nothing. Reuses the same
 * `/_assist/topic-insights` pipeline the Behavior topic pages use.
 */
export default function SpendCostInsights({ providerCosts, grandTotal, scopeKey }: SpendCostInsightsProps): ReactElement | null {
    const [insights, setInsights] = useState<TopicCallout[] | null>(null);
    const [generating, setGenerating] = useState(false);
    const request = useMemo(() => buildRequest(providerCosts, grandTotal), [providerCosts, grandTotal]);

    useEffect((): (() => void) => {
        let cancelled = false;
        setInsights(null);
        if (request.rows.length === 0) return () => undefined;
        setGenerating(true);
        void fetchTopicInsights(request)
            .then((reply) => {
                if (!cancelled && reply.insights.length > 0) setInsights(reply.insights);
            })
            .catch(() => undefined)
            .finally(() => {
                if (!cancelled) setGenerating(false);
            });
        return (): void => {
            cancelled = true;
        };
    }, [request, scopeKey]);

    if ((insights === null || insights.length === 0) && !generating) return null;

    return (
        <SurfaceCard $padding="md">
            <Group gap={6} mb="sm" wrap="nowrap" justify="space-between">
                <Group gap={6} wrap="nowrap">
                    <Sparkles size={14} style={{ color: 'var(--mantine-primary-color-filled)' }} />
                    <Text size="xs" fw={700} tt="uppercase" c="dimmed" style={{ letterSpacing: '0.06em' }}>
                        Insights
                    </Text>
                </Group>
                {generating && (
                    <Group gap={6} wrap="nowrap">
                        <Loader size={10} />
                        <Text size="xs" c="dimmed">
                            generating…
                        </Text>
                    </Group>
                )}
            </Group>
            {insights !== null && <CalloutList callouts={insights} accent="teal" />}
        </SurfaceCard>
    );
}
